// src/scenes/GameScene.js
// 🎮 GameScene - UPDATED: Manual question box collision checking
// Clean, maintainable scene that coordinates all game components

import Phaser from 'phaser';
import { gameState } from '../game/state.js';
import { GAME_CONFIG } from '../game/config.js';
import SpriteFactory from '../sprites/SpriteFactory.js';

// Import component managers
import BackgroundManager from './game-components/BackgroundManager.js';
import SpawningManager from './game-components/SpawningManager.js';
import PlayerController from './game-components/PlayerController.js';
import UIManager from './game-components/UIManager.js';
import CollisionHandler from './game-components/CollisionHandler.js';
import EffectsManager from './game-components/EffectsManager.js';

export default class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
    
    // Core game objects
    this.player = null;
    this.platforms = null;
    this.collectibles = null;
    this.obstacles = null;
    this.npcs = null;
    this.heartBoxes = null;
    this.ground = null;
    this.cursors = null;
    this.worldSpeed = GAME_CONFIG.PHYSICS.WORLD_SPEED;
    
    // Component managers
    this.backgroundManager = null;
    this.spawningManager = null;
    this.playerController = null;
    this.uiManager = null;
    this.collisionHandler = null;
    this.effectsManager = null;
    this.spriteFactory = null;
  }

  preload() {
    // Create sprite factory and generate all sprites
    this.spriteFactory = new SpriteFactory(this);
    this.spriteFactory.createAllSprites();
    
    /* console.log('🎨 All enhanced sprites preloaded for GameScene'); */
  }

  create() {
    /* console.log('🎮 GameScene: Creating modular Mario-style platformer...'); */
    
    // Initialize physics groups first
    this.createPhysicsGroups();
    
    // Initialize all component managers
    this.initializeManagers();
    
    // Setup the game world
    this.setupGameWorld();
    
    // Setup camera
    this.setupCamera();
    
    // Start the game systems
    this.startGameSystems();
    
    /* console.log(`✅ Enhanced Level ${gameState.level} ready!`); */
  }

  createPhysicsGroups() {
    this.platforms = this.physics.add.staticGroup();
    this.ground = this.physics.add.staticGroup();
    this.collectibles = this.physics.add.group();
    this.obstacles = this.physics.add.group();
    this.npcs = this.physics.add.group();
    this.heartBoxes = this.physics.add.staticGroup();
    
    /* console.log('🏗️ Physics groups created'); */
  }

  initializeManagers() {
    // Initialize all component managers with scene context
    this.backgroundManager = new BackgroundManager(this);
    this.spawningManager = new SpawningManager(this);
    this.playerController = new PlayerController(this);
    this.uiManager = new UIManager(this);
    this.collisionHandler = new CollisionHandler(this);
    this.effectsManager = new EffectsManager(this);
    
    /* console.log('🔧 Component managers initialized'); */
  }

  setupGameWorld() {
    // Create background and environment
    this.backgroundManager.createLevelBackground();
    this.backgroundManager.createGround();
    
    // Create player
    this.playerController.createPlayer();
    
    // Setup UI
    this.uiManager.createUI();
    
    // Setup controls
    this.playerController.setupControls();
    
    // Setup collisions
    this.collisionHandler.setupCollisions();
    
    /* console.log('🌍 Game world setup complete'); */
  }

  setupCamera() {
    const { width, height } = this.scale;
    
    this.cameras.main.startFollow(this.player, false, 0.08, 0.1);
    this.cameras.main.setFollowOffset(-150, 0);
    this.cameras.main.setDeadzone(50, height * 0.4);
    this.cameras.main.setBounds(-500, 0, width * 20, height);
    
    /* console.log('📹 Camera setup complete'); */
  }

  startGameSystems() {
    // Start spawning systems
    this.spawningManager.startSpawning();
    
    /* console.log('🎯 Game systems started'); */
  }

  update(time, delta) {
    if (!this.player || !this.player.active) return;
    
    // Check time limit
    const isTimeUp = gameState.updateTime(delta);
    if (isTimeUp) {
      this.gameOver();
      return;
    }
    
    // Update all systems
    this.playerController.update(time, delta);
    this.uiManager.update(time, delta);
    
    // REMOVED: Manual question box collision checking (now using collectibles system)
    // this.collisionHandler.checkQuestionBoxCollisions();
    
    this.updateWorldSpeed();
    this.checkLevelComplete();
  }

  updateWorldSpeed() {
    // Update world scrolling speed
    gameState.updateDistance(this.worldSpeed * 0.016);
    
    if (gameState.distanceTraveled % 500 < 1) {
      this.worldSpeed = Math.min(this.worldSpeed + 2, 200);
      this.spawningManager.updateWorldSpeed(this.worldSpeed);
    }
  }

  checkLevelComplete() {
    if (gameState.itemsCollected >= GAME_CONFIG.LEVELS.ITEMS_PER_LEVEL) {
      this.completeLevel();
    }
  }

  // === GAME STATE METHODS ===

  completeLevel() {
    /* console.log(`🎉 Level ${gameState.level} complete!`); */
    
    // Stop all spawning
    this.spawningManager.stopSpawning();
    
    // Complete level in game state
    gameState.completeLevel();
    
    // Level complete effect
    this.cameras.main.flash(500, 255, 255, 255);
    
    // Transition to next scene
    this.time.delayedCall(1000, () => {
      if (gameState.level >= 3) {
        this.scene.start('ScoreboardScene', { gameComplete: true });
      } else {
        this.scene.start('LevelCompleteScene');
      }
    });
  }

  gameOver() {
    /* console.log('💀 Game Over!'); */
    
    // Stop all systems
    this.spawningManager.stopSpawning();
    gameState.setState('GAME_OVER');
    
    // Game over effect
    this.cameras.main.fadeOut(1000, 0, 0, 0);
    
    this.time.delayedCall(1000, () => {
      this.scene.start('ScoreboardScene', { gameComplete: true });
    });
  }

  // === UTILITY METHODS FOR MANAGERS ===

  getWorldSpeed() {
    return this.worldSpeed;
  }

  getPlayer() {
    return this.player;
  }

  setPlayer(player) {
    this.player = player;
  }

  getPhysicsGroups() {
    return {
      platforms: this.platforms,
      ground: this.ground,
      collectibles: this.collectibles,
      obstacles: this.obstacles,
      npcs: this.npcs,
      heartBoxes: this.heartBoxes
    };
  }

  // === MANAGER ACCESS METHODS ===

  getEffectsManager() {
    return this.effectsManager;
  }

  getCollisionHandler() {
    return this.collisionHandler;
  }

  getSpawningManager() {
    return this.spawningManager;
  }
}